import logger from '../logger';
import { useState, useCallback } from 'react';
import { useAppointment } from '../../Context/Appointment/AppointmentContext';
import { useLocation } from '../../Context/Location/LocationContext';
import { useSchedule } from '../../Context/Schedule/ScheduleContext';
import { useCustomer } from '../../Context/Customer/CustomerContext';
import { OUTLET_TYPES } from '../bookingConfig';
import { computeMemberFlags } from '../memberPricing';
import { calculateEndTime } from '../Helper';
import { resolveBookingDateTime } from '../resolveBookingDateTime';


const getServicePrice = (service, isMembershipApplied, isMemberRateApplied, isClassicOutlet) => {
  if (!isMembershipApplied) return service?.price_rate?.nonMemberPrice || null;
  if (isMemberRateApplied) return service?.price_rate?.member_price_before_6_pm || null;
  return service?.price_rate?.member_after_6_or_weekends || null
  // Commented out: the following logic is not needed because the member rate is already determined by service?.price_rate?.member_after_6_or_weekends || null
  // return isClassicOutlet
  //   ? service?.price_rate?.member_after_6_or_weekends || null
  //   : service?.price_rate?.memberPrice || null;
};

export const buildMemberServiceData = (members, actualMembership, outletTypeID, selectedDate, selectedTime, outletTypeName, customerFirstName = '', isPublicHoliday = false) => {
    const primaryCustomerName = typeof customerFirstName === 'string' ? customerFirstName.trim() : '';
    const firstService = members.flatMap((member) => member.services || [])[0];
    const { isMembershipApplied, isMemberRateApplied } = computeMemberFlags(
      actualMembership, outletTypeID, selectedDate, selectedTime || firstService?.startTime, outletTypeName, isPublicHoliday
    );
    const isClassicOutlet = Number(outletTypeID) === OUTLET_TYPES.CLASSIC;
    // Pass the outlet name so Classic/Plus are recognized by name, not the ID fallback.
    const memberPriceFlags = computeMemberFlags(
      true, outletTypeID, selectedDate, selectedTime || firstService?.startTime, outletTypeName, isPublicHoliday
    );


    logger.log('buildMemberServiceData', {
      members,
      actualMembership,
      outletTypeID,
      selectedDate})

    const appointmentTime = resolveBookingDateTime(selectedDate, selectedTime).time;
    return members.flatMap((member) => {
      let cursor = appointmentTime;
      return member.services.map((service) => {
        const startTime = cursor || service?.startTime;
        const endTime = cursor
          ? calculateEndTime(cursor, parseInt(service?.duration, 10) || 0)
          : service?.endTime;
        cursor = appointmentTime ? endTime : '';
        return {
          service: service.id || '',
          duration: parseInt(service?.duration) || 0,
          start_time: startTime ? startTime.split(':').slice(0, 2).join(':') : '',
          end_time: endTime ? endTime.split(':').slice(0, 2).join(':') : '',
          therapist: service?.therapistData?.id || null,
          is_autoassigned: service?.therapistData?.id ? 0 : 1,
          requested_person: service?.therapistData?.id ? 1 : 0,
          // Website prices always use member rates; eligibility only sets flags.
          price: getServicePrice(service, true, memberPriceFlags.isMemberRateApplied, isClassicOutlet),
          isMembershipApplied,
          isMemberRateApplied,
          quantity: "1",
          customer_name: member.isPrimary && primaryCustomerName ? primaryCustomerName : member.name,
          user: member.user,
          primary: member.isPrimary ? 1 : 0,
          pair: member.id,
          item_number: member.id + 1,
          addons: (service?.selectedAddons || []).map((addon) => ({
            id: addon.id,
            name: addon.name,
            quantity: 1,
          })),
        };
      });
    });
};

export const useMemberServiceDataUpdater = (members, outletTypeName) => {
  const { actualMembership } = useAppointment();
  const { outletTypeID } = useLocation();
  const { selectedDate, selectedTime, isPublicHoliday } = useSchedule();
  const { formData } = useCustomer();
  const customerFirstName = formData?.fname;
  const [memberData, setMemberData] = useState([]);

  const updateMemberData = useCallback(() => {
    setMemberData(buildMemberServiceData(members, actualMembership, outletTypeID, selectedDate, selectedTime, outletTypeName, customerFirstName, isPublicHoliday));
  }, [members, actualMembership, outletTypeID, selectedDate, selectedTime, outletTypeName, customerFirstName, isPublicHoliday]);

  return { memberData, updateMemberData };
};
